import { supabase } from '@/lib/supabaseClient';
import {
  ReconciliationIssueRecord,
  IssueStatus,
  IssueSeverity,
  CompoundGrowerResolutionPayload,
  CycleReconciliationPayload,
  RateRuleResolutionPayload,
  CounterpartyResolutionPayload,
  DuplicateResolutionPayload,
  PreviousResolutionSnapshot,
  ProductionCycleRecord,
} from '@/types/database';

export interface IssueFilters {
  status?: IssueStatus;
  entity_type?: string;
  severity?: IssueSeverity;
  issue_type?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface IssueCounts {
  needsReview: number;
  resolved: number;
  historicalAccepted: number;
  total: number;
  critical: number;
  warning: number;
  info: number;
}

export interface GrowerImpactResult {
  growerName: string;
  cycleCount: number;
  cycles: Array<{
    id: string;
    cycle_code: string;
    batch_code: string;
    legacy_raiser_name?: string | null;
    status: string;
    start_date: string;
  }>;
}

export interface CycleCandidateRecords {
  cycle: ProductionCycleRecord | null;
  ledgerTransactions: Array<{
    id: string;
    account_code: string;
    transaction_date: string;
    amount: number;
    description: string;
    source_row?: number;
  }>;
  existingArrivals: Array<{
    id: string;
    arrival_date: string;
    head_count: number;
    total_weight_kg?: number;
    is_void: boolean;
  }>;
  existingSales: Array<{
    id: string;
    sale_date: string;
    head_count: number;
    total_amount: number;
    is_void: boolean;
  }>;
  unlinkedSalesCandidates: Array<{
    id: string;
    sale_date: string;
    head_count: number;
    total_amount: number;
    notes?: string;
  }>;
}

export const reconciliationService = {
  /**
   * Fetches paginated issues with filtering by status, entity type, severity, and search text.
   */
  async getIssues(filters: IssueFilters = {}): Promise<{ issues: ReconciliationIssueRecord[]; totalCount: number }> {
    const {
      status = 'needs_review',
      entity_type,
      severity,
      issue_type,
      search,
      page = 1,
      pageSize = 50,
    } = filters;

    let query = supabase
      .from('reconciliation_issues')
      .select(
        `
        id,
        entity_type,
        entity_id,
        cycle_id,
        legacy_value,
        source_sheet,
        source_rows,
        issue_type,
        severity,
        description,
        suggested_action,
        status,
        selected_decision,
        resolution_payload,
        resolution_notes,
        resolved_by,
        resolved_at,
        previous_resolutions,
        created_at,
        updated_at,
        production_cycles (
          id,
          cycle_code,
          batch_code,
          status,
          operation_type,
          start_date,
          legacy_raiser_name,
          growers (
            id,
            full_name
          ),
          farms (
            id,
            farm_name
          ),
          production_units (
            id,
            unit_name
          )
        )
      `,
        { count: 'exact' }
      )
      .eq('status', status);

    if (entity_type && entity_type !== 'all') {
      query = query.eq('entity_type', entity_type);
    }

    if (severity && severity !== ('all' as any)) {
      query = query.eq('severity', severity);
    }

    if (issue_type && issue_type !== 'all') {
      query = query.eq('issue_type', issue_type);
    }

    if (search && search.trim() !== '') {
      const s = search.trim();
      query = query.or(
        `legacy_value.ilike.%${s}%,description.ilike.%${s}%,issue_type.ilike.%${s}%,source_sheet.ilike.%${s}%`
      );
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query.order('created_at', { ascending: false }).range(from, to);

    const { data, error, count } = await query;
    if (error) {
      console.error('Error fetching reconciliation issues:', error);
      throw error;
    }

    return {
      issues: (data as any) || [],
      totalCount: count || 0,
    };
  },

  /**
   * Fetches live aggregate counts for the 3 queue tabs and severity levels.
   */
  async getIssueCounts(): Promise<IssueCounts> {
    const [nrRes, resRes, histRes, critRes, warnRes, infoRes] = await Promise.all([
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'needs_review'),
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'resolved'),
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'historical_incomplete_accepted'),
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'needs_review').eq('severity', 'critical'),
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'needs_review').eq('severity', 'warning'),
      supabase.from('reconciliation_issues').select('*', { count: 'exact', head: true }).eq('status', 'needs_review').eq('severity', 'info'),
    ]);

    const nr = nrRes.count || 0;
    const res = resRes.count || 0;
    const hist = histRes.count || 0;

    return {
      needsReview: nr,
      resolved: res,
      historicalAccepted: hist,
      total: nr + res + hist,
      critical: critRes.count || 0,
      warning: warnRes.count || 0,
      info: infoRes.count || 0,
    };
  },

  /**
   * Fetches single issue by ID with complete metadata and joined cycles.
   */
  async getIssueById(id: string): Promise<ReconciliationIssueRecord | null> {
    const { data, error } = await supabase
      .from('reconciliation_issues')
      .select(
        `
        id,
        entity_type,
        entity_id,
        cycle_id,
        legacy_value,
        source_sheet,
        source_rows,
        issue_type,
        severity,
        description,
        suggested_action,
        status,
        selected_decision,
        resolution_payload,
        resolution_notes,
        resolved_by,
        resolved_at,
        previous_resolutions,
        created_at,
        updated_at,
        production_cycles (
          id,
          cycle_code,
          batch_code,
          status,
          operation_type,
          start_date,
          expected_sale_date,
          legacy_raiser_name,
          growers (
            id,
            full_name
          ),
          farms (
            id,
            farm_name
          ),
          production_units (
            id,
            unit_name
          )
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching issue by ID:', error);
      return null;
    }
    return data as any;
  },

  /**
   * Calculates the impact preview of disambiguating a compound grower name
   * by finding all production cycles associated with this raw raiser string.
   */
  async getGrowerDisambiguationImpact(legacyRaiserName: string): Promise<GrowerImpactResult> {
    if (!legacyRaiserName) {
      return { growerName: '', cycleCount: 0, cycles: [] };
    }

    const { data, error } = await supabase
      .from('production_cycles')
      .select('id, cycle_code, batch_code, legacy_raiser_name, status, start_date')
      .or(`legacy_raiser_name.eq."${legacyRaiserName}",legacy_raiser_name.ilike."%${legacyRaiserName}%"`)
      .order('start_date', { ascending: false });

    if (error) {
      console.error('Error computing grower disambiguation impact:', error);
      return { growerName: legacyRaiserName, cycleCount: 0, cycles: [] };
    }

    const cycles = (data as any) || [];
    return {
      growerName: legacyRaiserName,
      cycleCount: cycles.length,
      cycles,
    };
  },

  /**
   * Fetches candidate operational records for cycle reconciliation (e.g. Sibal, Andres).
   */
  async getCycleCandidateRecords(cycleId: string): Promise<CycleCandidateRecords> {
    // 1. Fetch cycle
    const { data: cycleData } = await supabase
      .from('production_cycles')
      .select(
        `
        *,
        growers ( id, full_name ),
        farms ( id, farm_name ),
        production_units ( id, unit_name )
      `
      )
      .eq('id', cycleId)
      .single();

    if (!cycleData) {
      return {
        cycle: null,
        ledgerTransactions: [],
        existingArrivals: [],
        existingSales: [],
        unlinkedSalesCandidates: [],
      };
    }

    // 2. Fetch existing arrivals & sales for this cycle
    const [arrivalsRes, salesRes] = await Promise.all([
      supabase.from('pig_arrivals').select('id, arrival_date, head_count, total_weight_kg, is_void').eq('cycle_id', cycleId),
      supabase.from('pig_sales').select('id, sale_date, head_count, total_amount, is_void').eq('cycle_id', cycleId),
    ]);

    // 3. Fetch candidate unlinked sales matching grower name / batch code
    let unlinkedSalesCandidates: any[] = [];
    if (cycleData.legacy_raiser_name) {
      const { data: unlinkedSales } = await supabase
        .from('pig_sales')
        .select('id, sale_date, head_count, total_amount, notes')
        .is('cycle_id', null)
        .ilike('notes', `%${cycleData.legacy_raiser_name}%`)
        .limit(20);
      unlinkedSalesCandidates = unlinkedSales || [];
    }

    return {
      cycle: cycleData,
      ledgerTransactions: [],
      existingArrivals: arrivalsRes.data || [],
      existingSales: salesRes.data || [],
      unlinkedSalesCandidates,
    };
  },

  /**
   * Resolves a compound grower / pen disambiguation issue.
   * Updates normalized grower and production unit relationships across affected cycles.
   * Preserves raw legacy Excel strings in production_cycles.legacy_raiser_name.
   */
  async resolveCompoundGrower(
    issueId: string,
    payload: CompoundGrowerResolutionPayload,
    adminNotes?: string
  ): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    let targetGrowerId = payload.base_grower_id || payload.alias_grower_id || payload.created_grower_id;
    let targetUnitId = payload.created_unit_id;

    // 1. If decision is 'production_unit', create production unit if unit_name provided
    if (payload.decision === 'production_unit' && payload.unit_name && payload.base_grower_id) {
      // Check if unit already exists under this grower
      const { data: existingUnit } = await supabase
        .from('production_units')
        .select('id')
        .eq('grower_id', payload.base_grower_id)
        .ilike('unit_name', payload.unit_name.trim())
        .maybeSingle();

      if (existingUnit) {
        targetUnitId = existingUnit.id;
      } else {
        const { data: newUnit, error: unitError } = await supabase
          .from('production_units')
          .insert({
            grower_id: payload.base_grower_id,
            unit_name: payload.unit_name.trim(),
            is_active: true,
          })
          .select('id')
          .single();

        if (unitError) {
          console.error('Error creating production unit:', unitError);
          throw unitError;
        }
        targetUnitId = newUnit.id;
      }
    }

    // 2. If decision is 'co_grower' and new_grower_name provided, create new grower
    if (payload.decision === 'co_grower' && payload.new_grower_name) {
      const { data: newGrower, error: growerError } = await supabase
        .from('growers')
        .insert({
          full_name: payload.new_grower_name.trim(),
          is_active: true,
        })
        .select('id')
        .single();

      if (growerError) {
        console.error('Error creating co-grower:', growerError);
        throw growerError;
      }
      targetGrowerId = newGrower.id;
    }

    // 3. Update all affected production cycles
    if (payload.affected_cycle_ids && payload.affected_cycle_ids.length > 0 && targetGrowerId) {
      const updateData: any = { grower_id: targetGrowerId };
      if (targetUnitId) {
        updateData.production_unit_id = targetUnitId;
      }

      const { error: cycleUpdateError } = await supabase
        .from('production_cycles')
        .update(updateData)
        .in('id', payload.affected_cycle_ids);

      if (cycleUpdateError) {
        console.error('Error updating production cycles:', cycleUpdateError);
        throw cycleUpdateError;
      }
    }

    // 4. Update reconciliation issue record
    const updatedPayload = {
      ...payload,
      resolved_grower_id: targetGrowerId,
      resolved_unit_id: targetUnitId,
    };

    const { error: issueError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'resolved',
        selected_decision: payload.decision,
        resolution_payload: updatedPayload,
        resolution_notes: adminNotes || payload.notes || 'Resolved via Grower Disambiguation workflow',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (issueError) {
      console.error('Error resolving reconciliation issue:', issueError);
      throw issueError;
    }

    return true;
  },

  /**
   * Resolves a cycle reconciliation issue (e.g. Sibal, Andres / CYCLE-0771-2026_02).
   * Supports explicit placement logging, transfers, linking unlinked sales, or accepting historical incompleteness.
   * Keeps piglet source and operating relationship separate.
   */
  async resolveCycleIssue(
    issueId: string,
    cycleId: string,
    payload: CycleReconciliationPayload,
    adminNotes?: string
  ): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    // 1. Update cycle operation_type if explicitly specified
    if (payload.operating_relationship) {
      const { error: opError } = await supabase
        .from('production_cycles')
        .update({
          operation_type: payload.operating_relationship,
          updated_at: new Date().toISOString(),
        })
        .eq('id', cycleId);

      if (opError) {
        console.error('Error updating cycle operation_type:', opError);
        throw opError;
      }
    }

    // 2. Handle piglet placement / source
    if (payload.piglet_source_decision === 'internal_transfer') {
      // Represent internal transfer using transfer relationship
      if (payload.placed_head_count && payload.placed_head_count > 0) {
        const { error: transferError } = await supabase.from('pig_transfers').insert({
          destination_cycle_id: cycleId,
          source_unit_id: payload.source_farm_id || null,
          source_cycle_id: payload.source_cycle_id || null,
          head_count: payload.placed_head_count,
          transfer_date: payload.placement_date || new Date().toISOString().split('T')[0],
          reason: `Internal transfer documented via Review & Resolve${payload.source_farm_name ? ` from ${payload.source_farm_name}` : ''}`,
          is_void: false,
        });
        if (transferError) {
          console.error('Error creating pig transfer for internal placement:', transferError);
          throw transferError;
        }
      }
    } else if (payload.piglet_source_decision === 'commercial_placement') {
      // Explicit commercial placement confirmed with supporting source info
      if (payload.placed_head_count && payload.placed_head_count > 0) {
        const { error: arrivalError } = await supabase.from('pig_arrivals').insert({
          cycle_id: cycleId,
          supplier_id: payload.supplier_id || null,
          arrival_date: payload.placement_date || new Date().toISOString().split('T')[0],
          head_count: payload.placed_head_count,
          total_weight_kg: null,
          base_price_per_head: payload.base_price_per_head || null,
          is_void: false,
        });
        if (arrivalError) {
          console.error('Error creating pig arrival record:', arrivalError);
          throw arrivalError;
        }
      }
    } else if (payload.piglet_source_decision === 'cycle_transfer') {
      if (payload.source_cycle_id && payload.placed_head_count) {
        const { error: transferError } = await supabase.from('pig_transfers').insert({
          source_cycle_id: payload.source_cycle_id,
          destination_cycle_id: cycleId,
          head_count: payload.placed_head_count,
          transfer_date: payload.placement_date || new Date().toISOString().split('T')[0],
          reason: 'Inter-cycle pig transfer documented via Review & Resolve',
          is_void: false,
        });
        if (transferError) {
          console.error('Error logging cycle transfer:', transferError);
          throw transferError;
        }
      }
    } else if (payload.piglet_source_decision === 'link_sales' && payload.linked_sale_ids && payload.linked_sale_ids.length > 0) {
      const { error: salesLinkError } = await supabase
        .from('pig_sales')
        .update({ cycle_id: cycleId })
        .in('id', payload.linked_sale_ids);

      if (salesLinkError) {
        console.error('Error linking sales to cycle:', salesLinkError);
        throw salesLinkError;
      }
    }

    // 3. Mark reconciliation issue as resolved
    const { error: issueError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'resolved',
        selected_decision: payload.piglet_source_decision,
        resolution_payload: payload,
        resolution_notes: adminNotes || payload.notes || 'Resolved via Cycle Reconciliation workflow',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (issueError) {
      console.error('Error resolving cycle reconciliation issue:', issueError);
      throw issueError;
    }

    return true;
  },

  /**
   * Accepts historical incompleteness without inventing values or altering underlying transactions.
   */
  async acceptHistoricalIncomplete(issueId: string, notes?: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    const { error } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'historical_incomplete_accepted',
        selected_decision: 'historical_incomplete_accepted',
        resolution_notes: notes || 'Historical incompleteness accepted. Original evidence preserved without synthetic adjustment.',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (error) {
      console.error('Error accepting historical incompleteness:', error);
      throw error;
    }
    return true;
  },

  /**
   * Resolves a Yellow rate rule issue by activating or rejecting the rate rule.
   */
  async resolveRateRule(
    issueId: string,
    payload: RateRuleResolutionPayload,
    adminNotes?: string
  ): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    if (payload.rule_id) {
      const updateData: any = {
        is_active: payload.is_active,
        updated_at: new Date().toISOString(),
      };
      if (payload.rate_value !== undefined && payload.rate_value !== null) {
        updateData.rate_value = payload.rate_value;
      }
      if (payload.effective_start_date) {
        updateData.effective_start_date = payload.effective_start_date;
      }
      if (payload.effective_end_date) {
        updateData.effective_end_date = payload.effective_end_date;
      }

      const { error: ruleError } = await supabase
        .from('rate_rules')
        .update(updateData)
        .eq('id', payload.rule_id);

      if (ruleError) {
        console.error('Error updating rate rule:', ruleError);
        throw ruleError;
      }
    }

    const { error: issueError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'resolved',
        selected_decision: payload.decision,
        resolution_payload: payload,
        resolution_notes: adminNotes || payload.notes || 'Resolved via Rate Rule workflow',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (issueError) {
      console.error('Error resolving rate rule issue:', issueError);
      throw issueError;
    }

    return true;
  },

  /**
   * Resolves an unknown counterparty (supplier/buyer) issue.
   */
  async resolveCounterparty(
    issueId: string,
    payload: CounterpartyResolutionPayload,
    adminNotes?: string
  ): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    let targetId = payload.matched_id;

    if (payload.decision === 'create_new' && payload.new_name) {
      if (payload.counterparty_type === 'supplier') {
        const { data: newSupp, error: suppError } = await supabase
          .from('suppliers')
          .insert({
            supplier_name: payload.new_name.trim(),
            supplier_type: payload.new_category || 'general',
            is_active: true,
          })
          .select('id')
          .single();

        if (suppError) throw suppError;
        targetId = newSupp.id;
      } else {
        const { data: newBuyer, error: buyerError } = await supabase
          .from('buyers')
          .insert({
            buyer_name: payload.new_name.trim(),
            is_active: true,
          })
          .select('id')
          .single();

        if (buyerError) throw buyerError;
        targetId = newBuyer.id;
      }
    }

    const { error: issueError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'resolved',
        selected_decision: payload.decision,
        resolution_payload: { ...payload, resolved_id: targetId },
        resolution_notes: adminNotes || payload.notes || 'Resolved via Counterparty workflow',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (issueError) {
      console.error('Error resolving counterparty issue:', issueError);
      throw issueError;
    }

    return true;
  },

  /**
   * Resolves duplicate candidates non-destructively.
   * Keeps both or soft-voids duplicate record (never deletes).
   */
  async resolveDuplicate(
    issueId: string,
    payload: DuplicateResolutionPayload,
    adminNotes?: string
  ): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    if (payload.decision === 'soft_void' && payload.void_record_id && payload.void_table) {
      const allowedTables = ['expenses', 'pig_sales', 'feed_receipts', 'medicine_receipts', 'other_income_transactions'];
      if (allowedTables.includes(payload.void_table)) {
        const { error: voidError } = await supabase
          .from(payload.void_table as any)
          .update({
            is_void: true,
            void_reason: payload.void_reason || 'Duplicate transaction confirmed via Review & Resolve',
            voided_at: new Date().toISOString(),
            voided_by: userId,
            updated_at: new Date().toISOString(),
          })
          .eq('id', payload.void_record_id);

        if (voidError) {
          console.error('Error soft-voiding duplicate record:', voidError);
          throw voidError;
        }
      }
    }

    const { error: issueError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'resolved',
        selected_decision: payload.decision,
        resolution_payload: payload,
        resolution_notes: adminNotes || payload.notes || 'Resolved via Duplicate Review workflow',
        resolved_by: userId,
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (issueError) {
      console.error('Error resolving duplicate issue:', issueError);
      throw issueError;
    }

    return true;
  },

  /**
   * Reopens a previously resolved issue with full audit history preservation.
   * Appends the current resolution snapshot to previous_resolutions JSONB array
   * and resets status back to 'needs_review'.
   */
  async reopenIssue(issueId: string, reopenNotes?: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;

    // 1. Fetch current resolution state
    const { data: currentIssue, error: fetchError } = await supabase
      .from('reconciliation_issues')
      .select('resolved_at, resolved_by, selected_decision, resolution_payload, resolution_notes, previous_resolutions')
      .eq('id', issueId)
      .single();

    if (fetchError || !currentIssue) {
      console.error('Error fetching issue for reopen:', fetchError);
      throw fetchError || new Error('Issue not found');
    }

    // 2. Build snapshot
    const snapshot: PreviousResolutionSnapshot = {
      resolved_at: currentIssue.resolved_at || new Date().toISOString(),
      resolved_by: currentIssue.resolved_by,
      selected_decision: currentIssue.selected_decision || 'unspecified',
      resolution_payload: currentIssue.resolution_payload,
      resolution_notes: currentIssue.resolution_notes,
      reopened_at: new Date().toISOString(),
      reopened_by: userId,
      reopen_notes: reopenNotes || 'Reopened by administrator for re-evaluation',
    };

    const previousResolutions = Array.isArray(currentIssue.previous_resolutions)
      ? [...currentIssue.previous_resolutions, snapshot]
      : [snapshot];

    // 3. Reset issue to needs_review
    const { error: updateError } = await supabase
      .from('reconciliation_issues')
      .update({
        status: 'needs_review',
        selected_decision: null,
        resolution_payload: null,
        resolution_notes: null,
        resolved_by: null,
        resolved_at: null,
        previous_resolutions: previousResolutions,
        updated_at: new Date().toISOString(),
      })
      .eq('id', issueId);

    if (updateError) {
      console.error('Error reopening reconciliation issue:', updateError);
      throw updateError;
    }

    return true;
  },
};
